/**
 * tileFromSummary (HOF-270) — the same {@link TileModel}, built from the
 * MATERIALISED per-item summary instead of from the item's whole cell history.
 *
 * ``buildTileModel`` derives a tile by aggregating every observation the farm has
 * ever booked for the item: on Wetterauer Früchtchen that is 39.863 facts behind
 * four numbers and a shape, re-aggregated on every visit, growing forever. The
 * ``preisspiegel_tile`` derived fact carries the AGGREGATED INPUTS — the headline,
 * the *Stand*, the Quellen count, the YoY % and the pre-bucketed 120-day curve —
 * so this path is O(1) per tile.
 *
 * ── What deliberately did NOT move to the server ─────────────────────────────
 *
 * The drawing. {@link buildDatedSparkline} still lays out the points here, the gap
 * breaking is still its default, and {@link trendFromPct} still classifies the
 * trend — all three are pure, already tested (``sparklineGaps.test.ts``,
 * ``tileModel.test.ts``) and cost O(≤120 points). Materialising SVG path strings
 * would pin the tile's pixel geometry into customer twin data, so a viewBox change
 * would need a re-materialise; and every field mirrored server-side is a field the
 * lockstep parity fixture has to police (spec Assumption 3).
 *
 * ── The contract this file is one half of ───────────────────────────────────
 *
 * ``tests/fixtures/preisspiegel_tile_parity@v1.json`` pins, byte-exactly, that for
 * the same underlying facts:
 *
 *     buildTileModel(item, seriesFromCells)  ===  tileModelFromSummary(item, summary)
 *
 * asserted on BOTH sides — here in ``tileFromSummary.test.ts`` and in Python where
 * the fact is written (``test_twin_materialise_preisspiegel_tile.py``). If either
 * aggregation changes, regenerate that fixture and update both sides in the same
 * PR. Divergence is red CI, and divergence is this issue's core risk: the
 * Preisspiegel is the surface HOF-21 values a Leistung-Posten at, so a wrong
 * summary is a wrong margin, not merely a wrong tile.
 */
import type { PreisspiegelTileSummary, TileSummaryPoint } from '../../../services/twinService';
import type { TrackedItem } from './catalog';
import { buildDatedSparkline, buildSparkline, SPARKLINE_TILE_WINDOW_DAYS } from './sparkline';
import { trendFromPct, type TileModel } from './tileModel';

/**
 * Build the tile view-model for an item from its materialised summary.
 *
 * ``summary`` may be ``undefined`` — an item the catalog tracks and the farm has no
 * data for. That is the ordinary empty tile and is NOT the AC 17 fallback: an item
 * with no facts has no summary to be missing. The caller distinguishes the two (it
 * knows whether the item had data from the index), because only one of them means
 * the materialiser is broken.
 *
 * On an empty tile the unit is the CATALOG's — the one place #2944 still allows
 * that substitution, because there is no published fact to contradict and the
 * catalog unit is what the next write will carry.
 */
export function tileModelFromSummary(item: TrackedItem, summary: PreisspiegelTileSummary | undefined): TileModel {
  if (!summary || (summary.last_price == null && summary.last_at == null)) {
    return {
      itemId: item.id,
      name: item.name,
      unit: item.unit,
      unitMixed: false,
      ...(item.basis ? { basis: item.basis } : {}),
      state: 'empty',
      lastPrice: null,
      asOf: null,
      changePct: null,
      sparkline: buildSparkline([]),
      sourceCount: 0,
      estimated: false,
    };
  }

  return {
    itemId: item.id,
    name: item.name,
    // Straight from the fact. ``null`` is relayed as ``null`` — a tile whose facts
    // state no single unit prints none, and falling back to ``item.unit`` here is
    // exactly the #2944 substitution the whole published-unit rule exists to stop.
    unit: summary.unit as TileModel['unit'],
    unitMixed: summary.unit_mixed,
    ...(item.basis ? { basis: item.basis } : {}),
    state: trendFromPct(summary.change_pct),
    lastPrice: summary.last_price,
    asOf: summary.last_at,
    changePct: summary.change_pct,
    // Re-clipped to the window the FACT states, not to this module's constant: a
    // summary written under an older window must draw the curve it actually carries
    // rather than one silently re-cut to today's. The clip is idempotent when they
    // agree, which is the steady state.
    sparkline: buildDatedSparkline(
      summary.points.map((p) => ({ date: p.date, value: p.price })),
      { windowDays: summary.window_days ?? SPARKLINE_TILE_WINDOW_DAYS },
    ),
    sourceCount: summary.n_sources,
    estimated: summary.estimated,
  };
}

export type { PreisspiegelTileSummary, TileSummaryPoint };
